import { useMutation } from "@tanstack/react-query";
import { useNotifications } from "@via/hooks/useNotifications";
import axios from "axios";
import { GeoItem, GeoSearchResponse } from "common";
import {
  decideToFetchEna,
  manipulateGeoSearchResult,
} from "@via/pages/data/data-sources/api-data-sources/utils/helpers";

const ENTREZ_SEARCH_URL =
  "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi";

const ENTREZ_SUMMARY_URL =
  "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi";

const ENA_URL = "https://www.ebi.ac.uk/ena/portal/api/filereport";

const RETRY_COUNT = 3;

export const useGeoSearch = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({
      search,
      percentageCallback,
    }: {
      search: string;
      percentageCallback?: (percent: number) => void;
    }) => {
      const data = await initializeGeoSearch(search, percentageCallback);
      return { data, searchTerm: search };
    },
    onSuccess: ({ data, searchTerm }): void => {
      const hasResults = (data?.length ?? 0) > 0;

      const mainMessage = hasResults
        ? `Geo Search is finished successfully for ${searchTerm}`
        : `Geo search did not return any results for ${searchTerm}`;

      const detailMessage = hasResults
        ? "Files are loaded"
        : "Please check your accession ID and try again.";

      const messageVariant = hasResults
        ? NOTIFICATION.SUCCESS
        : NOTIFICATION.ALERT;

      setNotification({
        id: "geo-search-success-message",
        message: mainMessage,
        details: detailMessage,
        variant: messageVariant,
      });
    },
    onError: error => {
      setNotification({
        id: "geo-search-failed-message",
        message: "Geo Search Failed",
        details: `${error.message} - Files could not be loaded, please try again`,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

const fetchSraSummaryQuery = async (
  webenv: string,
  querykey: string,
  retriesCount = RETRY_COUNT
): Promise<{
  notFound: boolean;
  data: any[];
}> => {
  if (!webenv || !querykey) {
    return { notFound: true, data: [] };
  }
  const summaryUrl = `${ENTREZ_SUMMARY_URL}?db=sra&retmode=json&query_key=${querykey}&WebEnv=${webenv}&retstart=0&retmax=2000`;

  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        const { data } = await axios.get(summaryUrl, {
          withCredentials: false,
        });
        const { result } = data;
        if (!result) {
          resolve({
            notFound: true,
            data: [],
          });
        } else {
          const data = manipulateGeoSearchResult(result);
          const shouldFetchEna = decideToFetchEna(data);
          if (shouldFetchEna) {
            const enaResult = (await fetchEnaQuery(
              data as GeoItem[]
            )) as GeoItem[];
            resolve({
              notFound: false,
              data: enaResult,
            });
          }
          resolve({
            notFound: false,
            data,
          });
        }
      } catch (error) {
        if (retriesCount > 0) {
          return await fetchSraSummaryQuery(webenv, querykey, retriesCount - 1)
            .then(resolve)
            .catch(reject);
        }
        reject(error);
      }
    }, 600);
  });
};

// GSM414517 (no result is expected),
// GSE236077 (185 result is expected)
const initializeGeoSearch = async (
  search: string,
  percentageCallback?: (percent: number) => void
): Promise<any> => {
  const result: GeoItem[] = [];
  let notFound = false;
  let data: any[] | null = null;

  try {
    const { webenv, querykey } = await fetchSraQuery(search);
    const sraSummaryResult = await fetchSraSummaryQuery(webenv, querykey);
    notFound = sraSummaryResult.notFound;
    data = sraSummaryResult.data;
  } catch (error) {
    console.log(error);
    notFound = true;
  }
  if (notFound) {
    try {
      const data = await fetchGdsQuery(search, RETRY_COUNT, percentageCallback);
      result.push(...(data as GeoItem[]));
    } catch (error) {
      console.log(error);
    }
  }
  if (data) {
    result.push(...(data as GeoItem[]));
  }

  return result;
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const waitBeforeQuery = async (startDate: Date, maxWaitMs: number) => {
  const endDate = new Date();
  const timeDiff = endDate.getTime() - startDate.getTime(); //in ms
  const wait = maxWaitMs - timeDiff;
  if (wait > 0) {
    await delay(wait);
  }
};

const fetchSraQuery = async (
  geoId: string,
  retriesCount = RETRY_COUNT
): Promise<any> => {
  const requestStartDate = new Date();
  try {
    const baseUrl = `${ENTREZ_SEARCH_URL}?db=sra&usehistory=y&retmode=json&`;
    const searchUrl = `${baseUrl}term=${geoId}`;
    const { data } = await axios.get(searchUrl, {
      withCredentials: false,
    });
    const { webenv, querykey } = data?.esearchresult || {};
    return { webenv, querykey };
  } catch (error) {
    console.log(error);
    if (retriesCount > 0) {
      //3 query in 1000ms allowed by ncbi
      await waitBeforeQuery(requestStartDate, 1000);
      return await fetchSraQuery(geoId, retriesCount - 1);
    }
    throw geoId;
  }
};

const fetchGdsQuery = async (
  search: string,
  retriesCount = RETRY_COUNT,
  percentageCallback?: (percent: number) => void
): Promise<GeoItem[] | GeoSearchResponse[]> => {
  const requestStartDate = new Date();
  try {
    const baseUrl = `${ENTREZ_SEARCH_URL}?db=gds&usehistory=y&retmode=json&`;

    const searchUrl = `${baseUrl}term=${search}`;
    const { data: gdsData } = await axios.get(searchUrl, {
      withCredentials: false,
    });
    const { webenv, querykey } = gdsData?.esearchresult || {};
    const { notFound, data } = await fetchSraSummaryQuery(webenv, querykey);
    if (!notFound) {
      const result: GeoItem[] = [];
      let completedQueries = 0;
      const totalQueries = data.length;
      percentageCallback?.(completedQueries);
      for (const item of data) {
        const { webenv, querykey } = await fetchSraQuery(item.accession);
        const { data } = await fetchSraSummaryQuery(webenv, querykey);
        result.push(...data);
        completedQueries++;
        if (percentageCallback) {
          const percent = Math.round((completedQueries / totalQueries) * 100);
          percentageCallback(percent);
        }
      }
      return result;
    }
    return [];
  } catch (error) {
    if (retriesCount > 0) {
      //3 query in 1000ms allowed by ncbi
      await waitBeforeQuery(requestStartDate, 1000);
      return await fetchGdsQuery(search, retriesCount - 1, percentageCallback);
    }
    throw search;
  }
};

const fetchEnaQuery = async (data: GeoItem[]) => {
  try {
    const result: GeoItem[] = [];
    for (const item of data) {
      const { srrId } = item as GeoItem;
      const url = `${ENA_URL}?result=read_run&fields=fastq_ftp&format=JSON&accession=${srrId}`;
      const { data: enaData } = await axios.get(url, {
        withCredentials: false,
      });
      if (enaData?.length > 0) {
        if (enaData[0].fastq_ftp.includes(";")) {
          result.push({
            ...item,
            collectionType: "Paired",
          });
        } else {
          result.push({
            ...item,
            collectionType: "Single",
          });
        }
      } else {
        return data;
      }
    }
    return result;
  } catch (_err) {
    return data;
  }
};
